import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import {
  FastForwardSharp,
  FastRewindSharp,
  PlayArrow,
  PauseOutlined,
} from "@material-ui/icons";
import theme from "../../../assets/theme";

const formatTime = (time) => {
  if (isNaN(time)) return "00:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
};

const CustomAudioPlayerImage = ({ image, src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlayPause = () => {
    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const skipForward = () => {
    audioRef.current.currentTime += 10;
  };

  const skipBackward = () => {
    audioRef.current.currentTime -= 10;
  };

  const onTimeUpdate = () => {
    setProgress(audioRef.current.currentTime);
  };

  const onLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    const newTime = Number(e.target.value);
    audioRef.current.currentTime = newTime;
    setProgress(newTime);
  };

  useEffect(() => {
    const audio = audioRef.current;
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, []);

  return (
    <Container>
      <AudioBox image={image}>
        <audio ref={audioRef}>
          <source
            src={
              src
                ? src
                : "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
            }
            type="audio/mpeg"
          />
          Your browser does not support the audio element.
        </audio>

        <PlayerContainer>
          <DisplayContainer>
            <ButtonPlayer
              value="seek backward: alt j"
              title="seek backward: alt j"
              onClick={skipBackward}
            >
              <FastRewindSharp />
            </ButtonPlayer>
            <ButtonPlayer
              value="Play/Pause: alt k"
              title="Play/Pause: alt k"
              onClick={togglePlayPause}
            >
              {isPlaying ? <PauseOutlined /> : <PlayArrow />}
            </ButtonPlayer>
            <ButtonPlayer
              value="seek forward: alt l"
              title="seek forward: alt l"
              onClick={skipForward}
            >
              <FastForwardSharp />
            </ButtonPlayer>
          </DisplayContainer>
        </PlayerContainer>
        <ProgressContainer>
          <ProgressBar
            type="range"
            min="0"
            max={duration}
            value={progress}
            onChange={handleSeek}
            isFinished={progress >= duration}
          />
          <TimeDisplay>
            <span>{formatTime(progress)}</span>
            <span>{formatTime(duration)}</span>
          </TimeDisplay>
        </ProgressContainer>
      </AudioBox>
    </Container>
  );
};

export default CustomAudioPlayerImage;

const Container = styled.div`
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const AudioBox = styled.div`
  background: ${({ image }) =>
    image
      ? `url(${image}) no-repeat center center`
      : "rgba(255, 255, 255, 0.9)"};
  background-size: cover;
  background-position: center;
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  width: 100%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);

  @media (max-width: 480px) {
    width: 100%;
    height: 100%;
    padding: 1.5rem;
  }
  @media ${theme.breakpoints.sm_up} {
    height: 436px;
    width: 750px;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 1.5rem;
  width: 100%;
`;

const ProgressBar = styled.input`
  width: 100%;
  height: 8px;
  appearance: none;
  background: ${({ isFinished }) => (isFinished ? "#4caf50" : "#ccc")};
  border-radius: 5px;
  outline: none;
  cursor: pointer;
  transition: background 0.3s;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background: #4b4b4b;
    border-radius: 50%;
    cursor: pointer;
  }
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Poppins, sans-serif;
  font-size: 0.9rem;
  color: #333;
  margin-top: 0.5rem;
`;

const DisplayContainer = styled.div`
  display: flex;
  border-radius: 5px;
  width: 100%;
  @media ${theme.breakpoints.sm_down} {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-column-gap: 3px;
  }
`;

const ButtonPlayer = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  padding: 0.5em;
  border: 0;
  color: white;
  background: #4b4b4b;
  font-size: 1em;
  cursor: pointer;
  margin-right: 0.3rem;
  margin-top: 0.3rem;
  transition: background 0.3s ease;

  &:hover {
    background: #222;
  }
`;

const PlayerContainer = styled.div`
  display: inline-block;
  margin-right: 0.5em;
  margin-bottom: 0.5em;
  margin-top: 0.5rem;
  @media ${theme.breakpoints.sm_up} {
    margin-top: 15.5rem;
  }
`;
